import { ChevronDown, CornerDownRight } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import CellDropdown from 'src/components/CellDropdown';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import AddressTooltipContent from 'src/components/V3DropdownCmp/AddressTooltipContent';
import CarrierDetailTooltip from 'src/components/V3DropdownCmp/CarrierDetailTooltip';
import LineItemsTooltipContent from 'src/components/V3DropdownCmp/LineItemsTooltipContent';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { orderDetailsData } from 'src/redux/orders.slice';
import { RootState } from 'src/redux/store';
import { extractMcNumber, generateUnitSummary, getFormattedNumber, usePhone } from 'src/utils/CommonFunctions';

import { LEG_OVERVIEW_COLUMN_CLASSNAME_MAP } from './legOverview.const';
import { formatLegLocation, getLegDeliveryScheduleSource, getRouteTypeLabel, isCoveredLeg, resolveLegCarrierName } from './legOverview.helpers';
import { getLegStatusBadge, getStopStateBadge } from './legOverview.status';
import ScheduleTimeCell, { IInstructionEntry } from './ScheduleTimeCell';
import { IRouteOverviewHeadCell } from './useRouteOverviewColumns';

interface IProps {
    data: any;
    /** Every live leg of the order — engagement grouping and stop numbering need the siblings. */
    legs: any[];
    headCells: IRouteOverviewHeadCell[];
    selectedIds?: number[];
    onToggleSelect?: (id: number) => void;
}

/**
 * One row of the Route Overview table. Renders BOTH leg rows and nested stop rows (a covered leg —
 * the leg a carrier hauls on through after a stop): a stop row gets the ↳ arrow in the ID cell, a
 * stop-state badge, and blank checkbox / carrier / equipment cells, since those belong to the run.
 * Cells follow the head-cell order and skip hidden columns, so the column manager drives layout.
 */
const LegOverviewRow = ({ data, legs, headCells, selectedIds = [], onToggleSelect }: IProps) => {
    const { t } = useTranslation();
    const translate = t as (key: string, options?: Record<string, any>) => string;

    const descriptionRef = useRef<HTMLHeadingElement>(null);
    const [isCarrierTooltipOpen, setIsCarrierTooltipOpen] = useState(false);

    const isStopRow = isCoveredLeg(data);
    const originLocation = formatLegLocation(data?.shipper?.shipperAddress);
    const destinationLocation = formatLegLocation(data?.consignee?.consigneeAddress);
    const originAddress = data?.shipper?.shipperAddress?.fullAddress || originLocation;
    const destinationAddress = data?.consignee?.consigneeAddress?.fullAddress || destinationLocation;
    const carrierName = isStopRow ? '' : resolveLegCarrierName(data);

    // ── Carrier popover data — same rules as the Carrier & Pricing Details card (CarrierAssignment.tsx)
    // so the two never disagree about who the carrier is:
    //   isNoCarrierAssigned → no carrier, OR Carrier Names visibility is off (the backend's
    //                         maskOrderCarrier nulls carrierId) → present the ORG as the carrier.
    //   isUnAssignedCarrier → a carrierId exists but the carrier record didn't come back.
    // Contact rows are the customer's SALES REP (never the carrier's own contact) — the portal's
    // point of contact for a shipment is always their WAL rep. Share links have no logged-in
    // user, so the rep falls back to the salesRep* fields the order payload carries.
    const orgBranding = useOrgBranding();
    const currentUser = useSelector(currentUserSelector);
    const order = useSelector(orderDetailsData) as any;
    const { getFormatPhoneWithPlus } = usePhone();
    const carrierPrice = data?.carrierPrice;
    const carrier = carrierPrice?.carrier;
    const isNoCarrierAssigned = !carrierPrice || !carrierPrice.carrierId;
    const isUnAssignedCarrier = !!(carrierPrice && carrierPrice.carrierId && !carrier);
    const carrierImageSrc =
        !isNoCarrierAssigned && carrier ? (carrier.imageUrl && carrier.image ? `${carrier.imageUrl}${carrier.image}` : carrier.imageUrl || carrier.image || null) : orgBranding.logoSrc;
    const carrierMcId = isNoCarrierAssigned ? extractMcNumber(orgBranding.mcNumber) : isUnAssignedCarrier ? null : extractMcNumber(carrier?.mcId);
    const carrierTooltipName = isNoCarrierAssigned ? orgBranding.name : isUnAssignedCarrier ? t('Common.Unassigned_Carrier') : carrier?.name || carrierName;
    const salesRep = currentUser?.salesRep ?? {
        salesrepname: [order?.salesRepFirstName, order?.salesRepLastName].filter(Boolean).join(' ') || null,
        phone: order?.salesRepPhone || null,
        salesrepemail: order?.salesRepEmail || null,
    };
    const carrierContactDetails = [
        { label: t('Common.Contact_Name'), value: salesRep?.salesrepname },
        { label: t('Common.Contact_Number'), value: salesRep?.phone ? getFormatPhoneWithPlus(salesRep.phone) : null, copyValue: salesRep?.phone || undefined },
        { label: t('Common.Email_Address'), value: salesRep?.salesrepemail, copyValue: salesRep?.salesrepemail || undefined },
    ].filter((row) => Boolean(row.value));
    // Org presented as carrier with no uploaded logo → black WAL icon box (same fallback as the card).
    const orgLogoFallback = isNoCarrierAssigned ? (
        <div className="table-profile-icn-circle-xs rounded-md bg-utilityBlack flex items-center justify-center p-1 flex-none">
            <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
        </div>
    ) : undefined;
    const statusBadge = isStopRow ? getStopStateBadge(data, legs, translate) : getLegStatusBadge(data);
    const unitSummary = data?.order_dimensions?.length > 0 ? generateUnitSummary(data.order_dimensions) : '';
    // Handling-unit names for the Line Items card (ids → labels); loaded once app-wide by AppLayout.
    const handlingUnitOptions = useSelector((state: RootState) => state.basic.handlingUnitOptions);

    // Line Items card totals — same derivation as WALHQ's getLegLoadCellData. A collapsed engagement
    // row carries the run's merged dimensions, so its weight is summed from them rather than read from
    // the anchor leg's own totalWeight.
    const dims: any[] = data?.order_dimensions ?? [];
    const weightMeasurement = String(dims?.[0]?.weightMeasurement || 'lbs').toLowerCase();
    const totalLineItems = dims.reduce((sum: number, d: any) => sum + (Number(d?.handlingUnitNo) || 0), 0) || dims.length;
    const totalWeight = data?.totalWeight && !data?.isEngagementSummary ? Number(data.totalWeight) : dims.reduce((sum: number, d: any) => sum + (Number(d?.totalWeight) || 0), 0);
    const totalWeightDisplay = totalWeight ? `${getFormattedNumber(totalWeight, false)} ${weightMeasurement}` : '-';
    const totalLinearFt = data?.linearFootage ? `${data.linearFootage}` : '-';

    // Collapsed engagement rows (Show by Stop off) list one instruction per leg of the run, labelled by leg id.
    const engagementLegs: any[] = data?.isEngagementSummary ? data?.engagementLegs ?? [] : [];
    const toInstructionEntries = (field: 'pickupInstruction' | 'deliveryInstruction'): IInstructionEntry[] =>
        engagementLegs.filter((leg: any) => leg?.[field]).map((leg: any) => ({ label: leg?.legId ? `#${leg.legId}` : '-', text: leg[field] }));

    // Same cell as WALHQ's Route Overview: click the cell → address card (name, full address, copy).
    const locationCell = (id: 'origin' | 'destination', location: string, fullAddress: string, companyName?: string) => (
        <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP[id]} ${location ? 'table-cell-with-tooltip' : ''}`}>
            {location ? (
                <CellDropdown
                    wrapperClassName="flex items-center overflow-hidden h-full"
                    parentClassName="tooltip-content-with-scroll-wrap w-[350px] text-left space-y-1"
                    message={<AddressTooltipContent canCopyAddress shipperConsigneeName={companyName} fullAddress={fullAddress} />}
                >
                    <div className="overflow-hidden">
                        <h6 className="truncate">{location}</h6>
                    </div>
                </CellDropdown>
            ) : (
                '-'
            )}
        </td>
    );

    const cellMap: Record<string, React.ReactNode> = {
        id: (
            <td className={LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.id}>
                <div className="flex items-center gap-x-1 min-w-0">
                    {/* Indentation of a stop row comes from the arrow occupying the leading slot, not a padding class (same as WALHQ). */}
                    {isStopRow && <CornerDownRight className="w-3.5 h-3.5 text-grayLight600 flex-none" />}
                    <span className="font-medium truncate text-grayLight900">{data?.legId ? `#${data.legId}` : '-'}</span>
                </div>
            </td>
        ),
        routeType: <td className={LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.routeType}>{getRouteTypeLabel(data, legs, translate)}</td>,
        carrier: (
            <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.carrier} table-cell-with-tooltip `}>
                {isStopRow ? null : carrierName ? (
                    // With Carrier Names off the backend has already replaced the carrier by the org identity — shown as received.
                    <TooltipCmp
                        TooltipCustomClass="tooltipV3-content-wrap"
                        isArrow={false}
                        position="bottom"
                        openOnClick
                        isTooltipInteractive
                        parentClassName="tooltip-content-with-scroll-wrap w-[400px] text-left"
                        onOpenChange={setIsCarrierTooltipOpen}
                        message={
                            <CarrierDetailTooltip
                                imageUrl={carrierImageSrc}
                                name={carrierTooltipName}
                                contactLine={carrierMcId ? `MC #${carrierMcId}` : null}
                                imageFallbackNode={orgLogoFallback}
                                details={carrierContactDetails}
                            />
                        }
                    >
                        <div className={`flex items-center gap-x-1 h-full table-cell-clickable table-col-tooltip-space-common-xs cursor-pointer ${isCarrierTooltipOpen ? '!bg-utilityGray200' : ''}`}>
                            <h6 className="truncate">{carrierName}</h6>
                            <div className={`table-icon-wrap flex-none cursor-pointer flex items-center h-full p-1 ${isCarrierTooltipOpen ? '!opacity-100' : ''}`}>
                                <ChevronDown className="w-4 h-4 text-grayLight600 flex-none" />
                            </div>
                        </div>
                    </TooltipCmp>
                ) : (
                    <span className="table-col-tooltip-space-common-xs">-</span>
                )}
            </td>
        ),
        status: (
            <td className={LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.status}>
                {statusBadge ? (
                    <BadgeCmp style="modern" mainClassName="bg-white rounded-md" type={statusBadge.type}>
                        {statusBadge.label}
                    </BadgeCmp>
                ) : (
                    '-'
                )}
            </td>
        ),
        type: (
            <td className={LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.type}>
                {isStopRow ? null : data?.equipmentTypeFullName ? (
                    <BadgeCmp style="modern" type="pink" mainClassName="bg-white rounded-md">
                        {data.equipmentTypeFullName}
                    </BadgeCmp>
                ) : (
                    '-'
                )}
            </td>
        ),
        origin: locationCell('origin', originLocation, originAddress, data?.shipper?.companyName),
        destination: locationCell('destination', destinationLocation, destinationAddress, data?.consignee?.companyName),
        mode: <td className={LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.mode}>{t('Common.Road')}</td>,
        pickup_date: (
            <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.pickup_date} table-cell-with-tooltip`}>
                <ScheduleTimeCell
                    schedule={data?.pickupDeliverySchedule}
                    type="pickup"
                    heading={t('Orders.Route_Overview_Table.Pickup_Time')}
                    instructionTitle={t('Orders.Route_Overview_Table.Pickup_Instructions')}
                    instruction={data?.pickupInstruction}
                    instructionEntries={toInstructionEntries('pickupInstruction')}
                    businessHours={{ start: data?.shipper?.businessHoursStartTime, end: data?.shipper?.businessHoursEndTime }}
                />
            </td>
        ),
        delivery_date: (
            <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.delivery_date} table-cell-with-tooltip`}>
                <ScheduleTimeCell
                    schedule={getLegDeliveryScheduleSource(data)}
                    type="delivery"
                    heading={t('Orders.Route_Overview_Table.Delivery_Time')}
                    instructionTitle={t('Orders.Route_Overview_Table.Delivery_Instructions')}
                    instruction={data?.deliveryInstruction}
                    instructionEntries={toInstructionEntries('deliveryInstruction')}
                    businessHours={{ start: data?.consignee?.businessHoursStartTime, end: data?.consignee?.businessHoursEndTime }}
                />
            </td>
        ),
        // Same as WALHQ: the unit summary is the visible text, the Line Items table sits behind the chevron.
        description: (
            <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.description} ${unitSummary ? 'table-cell-with-tooltip' : ''}`}>
                {unitSummary ? (
                    <CellDropdown
                        wrapperClassName="flex items-center h-full"
                        parentClassName="tooltip-content-with-scroll-wrap min-w-[480px] space-y-2 text-left"
                        message={
                            <LineItemsTooltipContent
                                dimensions={dims}
                                weightMeasurement={weightMeasurement}
                                handlingUnitOptions={handlingUnitOptions}
                                totalWeightDisplay={totalWeightDisplay}
                                totalLineItems={totalLineItems}
                                totalLinearFt={totalLinearFt}
                            />
                        }
                    >
                        <TruncateTextCmp message={unitSummary} refName={descriptionRef}>
                            <h6 className="truncate min-w-0" ref={descriptionRef}>
                                {unitSummary}
                            </h6>
                        </TruncateTextCmp>
                    </CellDropdown>
                ) : (
                    '-'
                )}
            </td>
        ),
    };

    return (
        <>
            <td className={`${LEG_OVERVIEW_COLUMN_CLASSNAME_MAP.checkbox} sticky left-0 bg-inherit z-[1] table-cell-with-tooltip`} onClick={(e) => e.stopPropagation()}>
                {!isStopRow ? (
                    <CheckBox
                        id={`leg-select-${data?.id}`}
                        checked={selectedIds.includes(data?.id)}
                        onChangeFunc={() => onToggleSelect?.(data?.id)}
                        name={`leg-select-${data?.id}`}
                        value={String(data?.id)}
                        parentClassName="table-col-tooltip-space-common-xs h-full items-center"
                    />
                ) : (
                    <div className="table-col-tooltip-space-common-xs h-full items-center" />
                )}
            </td>
            {headCells
                .filter((cell) => cell.id !== 'checkbox' && cell.visible !== false && cellMap[cell.id])
                .map((cell) => (
                    <React.Fragment key={cell.id}>{cellMap[cell.id]}</React.Fragment>
                ))}
        </>
    );
};

export default LegOverviewRow;
